import React, { useMemo } from "react";
import SettingsService from "../services/settings-service";
import useFetch from "./useFetch";
import { useCacheActions } from "./useFetch";
import useOrganisation from "./useOrganisation";
import useOrganisationUtils from "./useOrganisationUtils";
import { Text, AnchorTextLink } from "@chaser/components";
import useUserInfo from "./useUserInfo";
import useUser from "./useUser";
import useFetchAllPlans from "./useFetchAllPlans";
import { BillingProvider, Modules } from "../util/constants";
import {
  isValidCurrency,
  DEFAULT_CURRENCY_FOR_UNSUPPORTED_CURRENCIES,
} from "../util/payments";

const noop = async () => {};

const ENTERPRISE_ONLY_BULLETS = [
  {
    id: "accountManagement",
    label: "Account management",
    enabled: true,
  },
  {
    id: "bespokeIntegrations",
    label: "Bespoke integrations",
    enabled: true,
  },
  {
    id: "multiEntity",
    label: "Multi-entity",
    enabled: true,
  },
];

function additionalEnterpriseBullets(bullets, planName) {
  const hasEnterpriseBullets = ENTERPRISE_ONLY_BULLETS.some((bullet) =>
    bullets.find((uniqueBullet) => uniqueBullet.id === bullet.id),
  );

  if (!hasEnterpriseBullets) {
    if (planName === "enterprise_v2") {
      return ENTERPRISE_ONLY_BULLETS;
    }

    return ENTERPRISE_ONLY_BULLETS.map((bullet) => ({
      ...bullet,
      enabled: false,
    }));
  }

  return [];
}

const displayedBullets = {
  creditReportAllowance: (_) => "Credit checking",
  sms: (_, enabled, __) =>
    enabled ? (
      <Text>
        SMS chasing (
        <AnchorTextLink href="https://help.chaserhq.com/sms-chasing">
          additional charges apply
        </AnchorTextLink>
        )
      </Text>
    ) : (
      "SMS chasing"
    ),
  feesAndDiscounts: (_) => "Early payment discounts",
  inAppCalls: (_) => "In-app calls",
  autoCall: (_) => "Auto-calls",
  taskList: (_) => "Task list",
  paymentPortal: (_) => "Payment Portal",
  escalatedSenders: "Escalated senders",
  domainVerification: "Custom sending domain",
  customerReport: "Customers report",
  letter: "Paper letters",
  revenueForecasting: (_) => "Revenue forecasting",
  artificialIntelligence: (_) => "Artificial Intelligence",
};

function getBullets(planFeatures, utils) {
  return Object.entries(displayedBullets).map(([key, format]) => {
    let enabled = true,
      n = -1;
    if (planFeatures[key] !== undefined) {
      n = planFeatures[key];
      enabled = n === 0 || !!n;
    } else if (/overage/i.test(key)) {
      enabled = false;
    }

    return {
      id: key,
      enabled,
      label: typeof format === "function" ? format(n, enabled, utils) : format,
    };
  });
}

function calculatePlans({
  availablePlans,
  invoicePlans,
  currencyCode,
  currentPlanPrice,
  currentPricePeriod,
  formatCurrency,
  helpers,
  isCancelled,
  isSuspended,
  modulePlan,
  modulePlanTitle,
  validators,
  BaseCurrency,
}) {
  if (!availablePlans || !invoicePlans || !Array.isArray(availablePlans)) {
    return [];
  }

  try {
    return availablePlans
      .map((plan) => {
        if (!plan || !plan.name || !plan.prices) return null;

        const { name, title: planTitle, prices } = plan;
        const planData = invoicePlans[name] || {};
        const currencyPrices = prices.filter(
          (price) => price.currency === currencyCode,
        );

        if (!planData || !planData.features) return null;

        const { title, features } = planData;
        const isCurrent =
          !(isSuspended || isCancelled) &&
          modulePlanTitle?.toLowerCase() === planTitle?.toLowerCase();

        const pricesByPeriod = currencyPrices.reduce((acc, price) => {
          if (isCurrent && price.period === currentPricePeriod) {
            acc[price.period] = {
              amount: currentPlanPrice / 100,
              formattedAmount: formatCurrency(
                currentPlanPrice / 100,
                currencyCode,
              ),
            };
            return acc;
          }

          acc[price.period] = {
            amount: price.amount / 100,
            formattedAmount: formatCurrency(price.amount / 100, currencyCode),
          };
          return acc;
        }, {});

        const bullets = features
          ? getBullets(features, { formatCurrency, currencyCode })
          : [];
        const enterpriseBullets = additionalEnterpriseBullets(bullets, name);
        bullets.push(...enterpriseBullets);

        let addons, scheduledPlan, scheduledDate, nextBillingDate;
        if (modulePlan) {
          ({ addons, scheduledPlan, scheduledDate, nextBillingDate } =
            modulePlan);
        }

        return {
          pricesByPeriod,
          title,
          features: features || {},
          bullets,
          invoices: features?.invoices || [],
          BaseCurrency,
          currencyCode,
          name,
          validators,
          helpers,
          addons,
          isCurrent,
          scheduledPlan,
          scheduledDate,
          nextBillingDate,
        };
      })
      .filter(Boolean)
      .sort(
        (a, b) =>
          (a.pricesByPeriod.month?.amount || 0) -
          (b.pricesByPeriod.month?.amount || 0),
      );
  } catch (error) {
    console.error("Error calculating plans:", error);
    return [];
  }
}

export default function useModuleInfo(
  moduleName = Modules.RECEIVABLES,
  options = {},
) {
  const { suspense = true } = options;
  const { BaseCurrency, ShortCode } = useOrganisation();
  const { isLoggedIn } = useUser();

  const [
    { data: moduleData, loading: loadingModule },
    { data: invoicePlans, loading: loadingInvoicePlans },
    { data: billingData, loading: loadingBilling },
  ] = [
    useFetch(
      ShortCode && isLoggedIn ? SettingsService.getModules : noop,
      ShortCode,
      {
        suspense,
      },
    ),
    useFetchAllPlans({ suspense }),
    useFetch(
      ShortCode && isLoggedIn ? SettingsService.getBillingInfo : noop,
      {
        ShortCode,
        moduleName,
      },
      {
        refetchOnMount: true,
        suspense,
      },
    ),
  ];

  const loading = loadingBilling || loadingInvoicePlans || loadingModule;

  const { formatCurrency } = useOrganisationUtils();
  const { invalidateDomain } = useCacheActions();

  const { isPartnerSubscribed, isSubscriber } = useUserInfo();

  const { subscription: module, invoicesChasedThisMonth } = moduleData || {};
  const { partner, availablePlans } = billingData || {};
  const {
    subscriber,
    isCancelled,
    isSuspended,
    plan: modulePlan,
  } = module || {};
  const moduleSubscriptions = useMemo(
    () =>
      (module?.moduleSubscriptions || []).map((moduleSubscription) => ({
        ...moduleSubscription,
      })),
    [module?.moduleSubscriptions],
  );

  const subscription = useMemo(() => {
    if (!moduleSubscriptions.length) return null;

    return (
      moduleSubscriptions.find((module) => module?.id === moduleName) || null
    );
  }, [moduleSubscriptions, moduleName]);

  const validators = useMemo(
    () => ({
      isPartnerSubscribed,
      isSubscriber: isSubscriber,
    }),
    [isPartnerSubscribed, isSubscriber],
  );

  const helpers = useMemo(
    () => ({
      partnerSubscriber:
        partner &&
        partner.subscriber &&
        `${partner.subscriber.firstName} ${partner.subscriber.lastName}`,
      subscriber:
        subscriber &&
        `${subscriber.profile.firstName} ${subscriber.profile.lastName}`,
    }),
    [partner, subscriber],
  );

  function refetchSubscriptionData() {
    invalidateDomain("organisation");
  }

  const plan = useMemo(() => {
    const organisationUsersAddon = moduleSubscriptions.find(
      (moduleSubscription) => moduleSubscription?.id === Modules.ORGANISATION,
    )?.addons?.users;

    if (moduleName === Modules.RECEIVABLES) {
      if (!module?.plan) return module?.plan;

      return {
        ...module.plan,
        addons: {
          ...(module.plan?.addons || {}),
          ...(organisationUsersAddon
            ? {
                users: organisationUsersAddon,
              }
            : {}),
        },
      };
    }

    if (!subscription) {
      return module?.plan;
    }

    return {
      title: subscription?.planName,
      name: subscription?.planName,
      amount: Number(subscription?.amountMinor || 0),
      period: subscription?.period || null,
      nextBillingDate:
        subscription?.nextBillingDate || module?.plan?.nextBillingDate || null,
      addons: subscription?.addons || {},
    };
  }, [moduleName, subscription, module?.plan, moduleSubscriptions]);

  const planPrice = plan?.amount || 0;
  const planPeriod = plan?.period;
  const currency = (module?.billingCurrency || BaseCurrency)?.toUpperCase();

  const currencyCode = isValidCurrency(
    BaseCurrency,
    module?.subscriptionService || BillingProvider.CHARGEBEE,
  )
    ? currency
    : DEFAULT_CURRENCY_FOR_UNSUPPORTED_CURRENCIES;

  const modulePlanTitle = plan?.title;

  const isSubscribed = subscription
    ? Boolean(subscription?.isActive)
    : !!(module?.plan && !module?.isCancelled);
  const isInTrial = subscription
    ? Boolean(subscription?.isInTrial)
    : Boolean(module?.isInTrial);

  const plans = useMemo(() => {
    return calculatePlans({
      availablePlans,
      invoicePlans,
      currencyCode,
      currentPlanPrice: planPrice,
      currentPricePeriod: planPeriod,
      formatCurrency,
      helpers,
      isCancelled,
      isSuspended,
      modulePlan,
      modulePlanTitle,
      validators,
      BaseCurrency,
    });
  }, [
    availablePlans,
    invoicePlans,
    currencyCode,
    planPrice,
    planPeriod,
    formatCurrency,
    helpers,
    isCancelled,
    isSuspended,
    modulePlan,
    modulePlanTitle,
    validators,
    BaseCurrency,
  ]);

  return {
    plans,
    plan,
    currentPlan: plan,
    period: planPeriod,
    helpers,
    validators,
    invoicesChasedThisMonth,
    module: {
      ...module,
      isSubscribed,
      isInTrial,
    },
    moduleSubscriptions,
    subscription,
    selectedModuleId: moduleName,
    currencyCode,
    subscriptionService: module?.subscriptionService,
    refetchSubscriptionData,
    loading,
  };
}
